import { useEffect, useRef } from "react";
import * as Cesium from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";

import type { TimedPoint } from "@/lib/replay-core";
import { colorForLap, type LapPassTimes } from "@/lib/lap-tracking";

interface Structure {
  latitude?: number;
  longitude?: number;
  altitude_m?: number;
  size_m?: number;
  structure_kind?: string;
  class_name?: string;
  yaw_deg?: number;
  pitch_deg?: number;
  roll_deg?: number;
  source_structure_id?: number | string;
  id?: number | string;
}

interface HomePoint {
  latitude: number;
  longitude: number;
  altitude_m?: number;
  label?: string;
}

export type CesiumCameraMode = "follow" | "fpv" | "free";
export type CesiumMapStyle = "photo" | "classic";

interface Cesium3DMapProps {
  points: TimedPoint[];
  color?: string;
  structures?: Structure[];
  homePoint?: HomePoint | null;
  ionToken?: string | null;
  height?: number | string;
  progressElapsed?: number;
  backgroundPoints?: { points: TimedPoint[]; color: string }[];
  lapPasses?: LapPassTimes;
  cameraMode?: CesiumCameraMode;
  fov?: number;
  mapStyle?: CesiumMapStyle;
}

function structureKey(structure: Structure): string | number | null {
  return structure.source_structure_id ?? structure.id ?? null;
}

function createRing(
  structure: Structure,
  segments = 72,
): Cesium.Cartesian3[] {
  if (structure.latitude == null || structure.longitude == null) return [];
  const radius = Math.max(Number(structure.size_m) || 20, 4) / 2;
  const positions: Cesium.Cartesian3[] = [];
  const centerCart = Cesium.Cartesian3.fromDegrees(
    structure.longitude,
    structure.latitude,
    structure.altitude_m ?? 0,
  );
  const frame = Cesium.Transforms.eastNorthUpToFixedFrame(centerCart);
  const rotation = Cesium.Matrix3.fromHeadingPitchRoll(
    new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(structure.yaw_deg ?? 0),
      Cesium.Math.toRadians(structure.pitch_deg ?? 0),
      Cesium.Math.toRadians(structure.roll_deg ?? 0),
    ),
  );
  for (let i = 0; i <= segments; i += 1) {
    const theta = (i / segments) * Math.PI * 2;
    const local = new Cesium.Cartesian3(
      Math.cos(theta) * radius,
      0,
      Math.sin(theta) * radius,
    );
    const rotated = Cesium.Matrix3.multiplyByVector(
      rotation,
      local,
      new Cesium.Cartesian3(),
    );
    const world = Cesium.Matrix4.multiplyByPoint(
      frame,
      rotated,
      new Cesium.Cartesian3(),
    );
    positions.push(world);
  }
  return positions;
}

export function Cesium3DMap({
  points,
  color = "#FF6B1A",
  structures,
  homePoint,
  ionToken,
  height = 520,
  progressElapsed,
  backgroundPoints,
  lapPasses,
  cameraMode = "follow",
  fov = 70,
  mapStyle = "photo",
}: Cesium3DMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const progressRef = useRef<{
    entity: Cesium.Entity | null;
    marker: Cesium.Entity | null;
    body: Cesium.Entity | null;
    canopy: Cesium.Entity | null;
    nose: Cesium.Entity | null;
    propsFL: Cesium.Entity | null;
    propsFR: Cesium.Entity | null;
    propsRL: Cesium.Entity | null;
    propsRR: Cesium.Entity | null;
    positions: Cesium.Cartesian3[];
  }>({
    entity: null,
    marker: null,
    body: null,
    canopy: null,
    nose: null,
    propsFL: null,
    propsFR: null,
    propsRL: null,
    propsRR: null,
    positions: [],
  });
  // Live pose read each frame by the drone body's CallbackProperty — updating
  // this ref (not the entity fields directly) is what keeps the orientation
  // animating smoothly during playback instead of only snapping when paused.
  const dronePoseRef = useRef<{
    pos: Cesium.Cartesian3;
    orientation: Cesium.Quaternion;
    canopy: Cesium.Cartesian3;
    propFR: Cesium.Cartesian3;
    propFL: Cesium.Cartesian3;
    propRR: Cesium.Cartesian3;
    propRL: Cesium.Cartesian3;
    show: boolean;
  }>({
    pos: new Cesium.Cartesian3(),
    orientation: new Cesium.Quaternion(),
    canopy: new Cesium.Cartesian3(),
    propFR: new Cesium.Cartesian3(),
    propFL: new Cesium.Cartesian3(),
    propRR: new Cesium.Cartesian3(),
    propRL: new Cesium.Cartesian3(),
    show: true,
  });
  const cameraModeRef = useRef<CesiumCameraMode>(cameraMode);
  cameraModeRef.current = cameraMode;
  // Follow-camera angular offset from the default "behind the drone" position.
  // Mutated by mouse drag in follow mode; `applyFollowCameraRef` reads this
  // each tick (and on drag) so the camera can be orbited without disrupting
  // playback. Pitch is stored in degrees, yaw in radians.
  const followOffsetRef = useRef<{ yaw: number; pitch: number }>({
    yaw: 0,
    pitch: 0,
  });
  // Snapshot of the latest playback pose used by the follow camera. Updated
  // each progress tick; read by `applyFollowCameraRef` so mouse drags can
  // re-frame the camera even when playback is paused.
  const followContextRef = useRef<{
    pos: Cesium.Cartesian3;
    headingRad: number;
  } | null>(null);
  const applyFollowCameraRef = useRef<() => void>(() => {});
  const backgroundProgressRef = useRef<
    Array<{
      points: TimedPoint[];
      positions: Cesium.Cartesian3[];
      marker: Cesium.Entity | null;
    }>
  >([]);
  const checkpointEntitiesRef = useRef<Map<string | number, Cesium.Entity>>(
    new Map(),
  );
  // Cesium reads the live elapsed value via CallbackProperty each frame so the
  // gate ring colors stay in sync without thrashing material assignment.
  const elapsedRef = useRef<number>(progressElapsed ?? 0);
  const lapPassesRef = useRef<LapPassTimes | undefined>(lapPasses);

  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return;

    const viewer = new Cesium.Viewer(containerRef.current, {
      animation: false,
      timeline: false,
      homeButton: false,
      infoBox: false,
      sceneModePicker: false,
      baseLayerPicker: false,
      geocoder: false,
      navigationHelpButton: false,
      fullscreenButton: false,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      shouldAnimate: false,
      baseLayer: false as unknown as Cesium.ImageryLayer,
    });

    (viewer as unknown as { _showRenderLoopErrors?: boolean })._showRenderLoopErrors = false;
    viewer.imageryLayers.removeAll();

    // Give the orbit controls a tighter, more responsive feel for compare /
    // free-look: allow much closer zoom, keep rotation inertia, and bind
    // left-drag to orbit (default is pan, which makes inspecting a route awkward
    // without a middle mouse button).
    const ssc = viewer.scene.screenSpaceCameraController;
    ssc.minimumZoomDistance = 5;
    ssc.maximumZoomDistance = 200000;
    ssc.inertiaSpin = 0.9;
    ssc.inertiaTranslate = 0.9;
    ssc.inertiaZoom = 0.85;
    ssc.rotateEventTypes = Cesium.CameraEventType.LEFT_DRAG;
    ssc.translateEventTypes = [
      Cesium.CameraEventType.MIDDLE_DRAG,
      { eventType: Cesium.CameraEventType.LEFT_DRAG, modifier: Cesium.KeyboardEventModifier.SHIFT },
    ];
    ssc.tiltEventTypes = [
      Cesium.CameraEventType.RIGHT_DRAG,
      Cesium.CameraEventType.PINCH,
      { eventType: Cesium.CameraEventType.LEFT_DRAG, modifier: Cesium.KeyboardEventModifier.CTRL },
    ];
    ssc.zoomEventTypes = [
      Cesium.CameraEventType.WHEEL,
      Cesium.CameraEventType.PINCH,
    ];

    // Modern atmospheric sky — re-enable atmosphere with a tactical blue tint.
    viewer.scene.globe.enableLighting = false;
    if (viewer.scene.skyAtmosphere) {
      viewer.scene.skyAtmosphere.show = true;
      // Tactical blue/teal hue — readable backdrop for orange route + HUD
      viewer.scene.skyAtmosphere.hueShift = -0.06;
      viewer.scene.skyAtmosphere.saturationShift = -0.05;
      viewer.scene.skyAtmosphere.brightnessShift = -0.12;
    }
    if (viewer.scene.skyBox) {
      (viewer.scene.skyBox as unknown as { show: boolean }).show = true;
    }
    if (viewer.scene.sun) viewer.scene.sun.show = false;
    if (viewer.scene.moon) viewer.scene.moon.show = false;
    viewer.scene.backgroundColor = Cesium.Color.fromCssColorString("#0F1B2D");
    viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString("#1A2435");
    viewer.scene.fog.enabled = true;
    viewer.scene.fog.density = 0.00008;

    const keepRendering = () => {
      if (!viewer.isDestroyed()) viewer.scene.requestRender();
    };
    viewer.scene.renderError.addEventListener(keepRendering);
    (viewer as unknown as { _renderErrorHandler?: () => void })._renderErrorHandler =
      keepRendering;
    viewer.useDefaultRenderLoop = true;

    viewerRef.current = viewer;

    return () => {
      if (!viewer.isDestroyed()) viewer.destroy();
      viewerRef.current = null;
    };
    // Viewer is created once on mount. Ion token arrives asynchronously from
    // useQuery; upgrading terrain is handled in a separate effect so the token
    // landing doesn't tear down the viewer (which wipes scene + imagery).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !ionToken) return;
    Cesium.Ion.defaultAccessToken = ionToken;
    if (!Cesium.createWorldTerrainAsync) return;
    let cancelled = false;
    (async () => {
      try {
        const terrain = await Cesium.createWorldTerrainAsync({
          requestVertexNormals: true,
        });
        if (cancelled || viewer.isDestroyed()) return;
        viewer.terrainProvider = terrain;
        viewer.scene.globe.depthTestAgainstTerrain = true;
        viewer.scene.requestRender();
      } catch {
        /* keep ellipsoid terrain */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ionToken]);

  // Build static scene (route, checkpoints, home) when inputs change
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    viewer.entities.removeAll();
    progressRef.current = {
      entity: null,
      marker: null,
      body: null,
      canopy: null,
      nose: null,
      propsFL: null,
      propsFR: null,
      propsRL: null,
      propsRR: null,
      positions: [],
    };
    backgroundProgressRef.current = [];

    const isPlaybackMode = progressElapsed !== undefined;

    backgroundPoints?.forEach((bg) => {
      if (!bg.points.length) return;
      const bgPositions = bg.points.map((p) =>
        Cesium.Cartesian3.fromDegrees(p.longitude, p.latitude, p.altitude_m ?? 0),
      );
      const bgPreview = Cesium.Color.fromCssColorString(bg.color).withAlpha(0.35);
      // Dashed preview of the full background route.
      viewer.entities.add({
        polyline: {
          positions: bgPositions,
          width: 3,
          material: bgPreview,
          depthFailMaterial: bgPreview,
        },
      });

      if (isPlaybackMode) {
        const bgBright = Cesium.Color.fromCssColorString(bg.color);
        const trail: Cesium.Cartesian3[] = [bgPositions[0]];
        viewer.entities.add({
          polyline: {
            positions: new Cesium.CallbackProperty(() => trail, false),
            width: 4,
            material: bgBright,
            depthFailMaterial: bgBright.withAlpha(0.9),
            arcType: Cesium.ArcType.NONE,
          },
        });
        const bgMarker = viewer.entities.add({
          position: bgPositions[0],
          point: {
            pixelSize: 12,
            color: Cesium.Color.fromCssColorString("#E8ECF1"),
            outlineColor: bgBright,
            outlineWidth: 2,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            heightReference: Cesium.HeightReference.NONE,
          },
        });
        backgroundProgressRef.current.push({
          points: bg.points,
          positions: trail,
          marker: bgMarker,
        });
      }
    });

    checkpointEntitiesRef.current.clear();
    structures?.forEach((structure, idx) => {
      if (
        structure.structure_kind !== "checkpoint_ring" ||
        structure.latitude == null ||
        structure.longitude == null
      ) {
        return;
      }
      const ringPositions = createRing(structure);
      if (!ringPositions.length) return;
      const key = structureKey(structure);
      // CallbackProperty so Cesium re-evaluates the color every render frame —
      // gates change the moment the drone clears them, without per-tick React
      // material reassignment.
      const colorProperty = new Cesium.CallbackProperty(() => {
        const passes = key !== null ? lapPassesRef.current?.get(key) ?? [] : [];
        let lap = 0;
        for (const t of passes) {
          if (t <= elapsedRef.current) lap += 1;
          else break;
        }
        return Cesium.Color.fromCssColorString(
          lap > 0 ? colorForLap(lap) : "#FF6B1A",
        );
      }, false);
      const ringMaterial = new Cesium.ColorMaterialProperty(
        colorProperty as unknown as Cesium.Property,
      );
      const entity = viewer.entities.add({
        polyline: {
          positions: ringPositions,
          width: 4,
          material: ringMaterial,
          depthFailMaterial: ringMaterial,
          clampToGround: false,
        },
        label: {
          text: `CP ${idx + 1}`,
          font: "12px sans-serif",
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.fromCssColorString("#0A0E14"),
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -12),
        },
        position: Cesium.Cartesian3.fromDegrees(
          structure.longitude,
          structure.latitude,
          structure.altitude_m ?? 0,
        ),
      });
      if (key !== null) checkpointEntitiesRef.current.set(key, entity);
    });

    if (homePoint) {
      viewer.entities.add({
        position: Cesium.Cartesian3.fromDegrees(
          homePoint.longitude,
          homePoint.latitude,
          homePoint.altitude_m ?? 0,
        ),
        point: {
          pixelSize: 10,
          color: Cesium.Color.fromCssColorString("#38BDF8"),
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2,
        },
        label: {
          text: homePoint.label ?? "Home",
          font: "12px sans-serif",
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.fromCssColorString("#0A0E14"),
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -14),
        },
      });
    }

    if (points.length >= 2) {
      const positions = points.map((p) =>
        Cesium.Cartesian3.fromDegrees(p.longitude, p.latitude, p.altitude_m ?? 0),
      );
      const routeColor = Cesium.Color.fromCssColorString(color);

      // Background full-route line — desaturated tactical blue so it reads as
      // "course preview" and doesn't compete with the bright orange flown line.
      const bgColor = Cesium.Color.fromCssColorString("#5A7FA8").withAlpha(0.55);
      viewer.entities.add({
        polyline: {
          positions,
          width: 3,
          material: bgColor,
          depthFailMaterial: bgColor,
        },
      });

      // Start marker
      viewer.entities.add({
        position: positions[0],
        point: {
          pixelSize: 10,
          color: routeColor,
          outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
          outlineWidth: 2,
        },
        label: {
          text: "Start",
          font: "11px sans-serif",
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.fromCssColorString("#0A0E14"),
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -12),
        },
      });
      // End marker
      viewer.entities.add({
        position: positions[positions.length - 1],
        point: {
          pixelSize: 9,
          color: Cesium.Color.fromCssColorString("#EF4444"),
          outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
          outlineWidth: 2,
        },
      });

      const initialProgress: Cesium.Cartesian3[] = isPlaybackMode
        ? [positions[0]]
        : [...positions];

      // Seed the drone pose ref with valid values so the body/canopy/nose/props
      // entities have a real Cartesian3 + identity Quaternion on their first
      // render frame. Without this seed, Cesium's render path hits the default
      // `new Cesium.Cartesian3()` (origin) and zero-Quaternion, which throws
      // "Cannot read properties of undefined (reading 'longitude')" in
      // GeographicProjection.project and halts the whole canvas.
      dronePoseRef.current.pos = Cesium.Cartesian3.clone(positions[0]);
      dronePoseRef.current.orientation = Cesium.Quaternion.clone(
        Cesium.Quaternion.IDENTITY,
      );
      dronePoseRef.current.canopy = Cesium.Cartesian3.clone(positions[0]);
      dronePoseRef.current.propFL = Cesium.Cartesian3.clone(positions[0]);
      dronePoseRef.current.propFR = Cesium.Cartesian3.clone(positions[0]);
      dronePoseRef.current.propRL = Cesium.Cartesian3.clone(positions[0]);
      dronePoseRef.current.propRR = Cesium.Cartesian3.clone(positions[0]);
      dronePoseRef.current.show = isPlaybackMode;

      // Progress polyline — bright orange showing flown segment
      const progressEntity = viewer.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(() => initialProgress, false),
          width: 5,
          material: routeColor,
          depthFailMaterial: routeColor.withAlpha(0.92),
          arcType: Cesium.ArcType.NONE,
        },
      });
      const marker = viewer.entities.add({
        position: isPlaybackMode ? positions[0] : positions[positions.length - 1],
        point: {
          pixelSize: 10,
          color: Cesium.Color.fromCssColorString("#E8ECF1"),
          outlineColor: routeColor,
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          heightReference: Cesium.HeightReference.NONE,
        },
      });
      // Oriented drone body — a small quad rendered with 1 central body box and
      // 4 propeller discs. The pose ref (dronePoseRef) is updated every playback
      // tick and read each frame by CallbackProperty so the visualization moves
      // continuously instead of only when playback pauses.
      //
      // Box primitives in Cesium use ENU-aligned local axes (X=east, Y=north,
      // Z=up at orientation identity), so we model:
      //   body X = RIGHT WING (east when heading=0)
      //   body Y = FORWARD / NOSE (north when heading=0)
      //   body Z = UP
      // Dimensions are (wingspan, length, height); prop offsets use
      // (±right, ±forward, +up). Note: because Cesium's HPR is designed for
      // glTF models (forward=+X), we swap pitch↔roll when constructing HPR —
      // see the progress/camera effect below.
      const bodyColor = Cesium.Color.fromCssColorString("#0F1B2D").withAlpha(0.95);
      const bodyEdge = Cesium.Color.fromCssColorString("#FF6B1A").withAlpha(0.95);
      const propFrontColor = Cesium.Color.fromCssColorString("#FF6B1A").withAlpha(0.9);
      const propRearColor = Cesium.Color.fromCssColorString("#E8ECF1").withAlpha(0.9);
      const noseColor = Cesium.Color.fromCssColorString("#FF6B1A").withAlpha(0.95);
      // Cyan canopy sits on top of the body so a roll or flip is obvious —
      // watch the canopy swing off-axis to read roll, and tilt forward/back
      // relative to the body to read pitch.
      const canopyColor = Cesium.Color.fromCssColorString("#38BDF8").withAlpha(0.92);
      const posCallback = new Cesium.CallbackProperty(
        () => dronePoseRef.current.pos,
        false,
      ) as unknown as Cesium.PositionProperty;
      const orientCallback = new Cesium.CallbackProperty(
        () => dronePoseRef.current.orientation,
        false,
      ) as unknown as Cesium.Property;
      // Cesium's TS types mark Entity.show as `boolean`, but at runtime it
      // accepts a Property too — cast through unknown to satisfy the checker.
      const showCallback = new Cesium.CallbackProperty(
        () => dronePoseRef.current.show,
        false,
      ) as unknown as boolean;
      const body = viewer.entities.add({
        position: posCallback,
        orientation: orientCallback,
        show: showCallback,
        box: {
          dimensions: new Cesium.Cartesian3(1.3, 1.5, 0.3),
          material: bodyColor,
          outline: true,
          outlineColor: bodyEdge,
          outlineWidth: 2.4,
        },
      });
      // Canopy — small cyan dome on the TOP of the airframe. Its vertical
      // position is body +Z so if the drone rolls right, the canopy visibly
      // leans to the right in world space; a flip puts it underneath.
      const canopy = viewer.entities.add({
        position: new Cesium.CallbackProperty(
          () => dronePoseRef.current.canopy,
          false,
        ) as unknown as Cesium.PositionProperty,
        orientation: orientCallback,
        show: showCallback,
        box: {
          dimensions: new Cesium.Cartesian3(0.65, 0.85, 0.28),
          material: canopyColor,
          outline: true,
          outlineColor: Cesium.Color.WHITE.withAlpha(0.95),
          outlineWidth: 2,
        },
      });
      // Nose indicator — a bright orange wedge sticking out of the front of the
      // body. Placed along body +Y (forward), so from the follow camera you
      // can always see which end is the front.
      const nose = viewer.entities.add({
        position: new Cesium.CallbackProperty(() => {
          const pose = dronePoseRef.current;
          const rot = Cesium.Matrix3.fromQuaternion(pose.orientation);
          const offset = Cesium.Matrix3.multiplyByVector(
            rot,
            new Cesium.Cartesian3(0, 1.05, 0),
            new Cesium.Cartesian3(),
          );
          return Cesium.Cartesian3.add(pose.pos, offset, new Cesium.Cartesian3());
        }, false) as unknown as Cesium.PositionProperty,
        orientation: orientCallback,
        show: showCallback,
        box: {
          dimensions: new Cesium.Cartesian3(0.55, 0.75, 0.34),
          material: noseColor,
          outline: true,
          outlineColor: Cesium.Color.WHITE.withAlpha(0.9),
          outlineWidth: 2,
        },
      });
      const makeProp = (front: boolean, key: "propFR" | "propFL" | "propRR" | "propRL") =>
        viewer.entities.add({
          position: new Cesium.CallbackProperty(
            () => dronePoseRef.current[key],
            false,
          ) as unknown as Cesium.PositionProperty,
          orientation: orientCallback,
          show: showCallback,
          ellipsoid: {
            radii: new Cesium.Cartesian3(0.55, 0.55, 0.08),
            material: front ? propFrontColor : propRearColor,
            outline: true,
            outlineColor: Cesium.Color.WHITE.withAlpha(0.85),
          },
        });
      const propsFL = makeProp(true, "propFL");
      const propsFR = makeProp(true, "propFR");
      const propsRL = makeProp(false, "propRL");
      const propsRR = makeProp(false, "propRR");
      progressRef.current = {
        entity: progressEntity,
        marker,
        body,
        canopy,
        nose,
        propsFL,
        propsFR,
        propsRL,
        propsRR,
        positions: initialProgress,
      };

      // Include checkpoint rings + background routes in the initial bounding
      // sphere so the camera defaults frame the whole scene, not just the
      // primary route (important for compare view where the two flights may
      // diverge).
      const framingPositions: Cesium.Cartesian3[] = [...positions];
      backgroundPoints?.forEach((bg) => {
        bg.points.forEach((p) => {
          framingPositions.push(
            Cesium.Cartesian3.fromDegrees(p.longitude, p.latitude, p.altitude_m ?? 0),
          );
        });
      });
      structures?.forEach((s) => {
        if (
          s.structure_kind !== "checkpoint_ring" ||
          s.latitude == null ||
          s.longitude == null
        ) {
          return;
        }
        framingPositions.push(
          Cesium.Cartesian3.fromDegrees(s.longitude, s.latitude, s.altitude_m ?? 0),
        );
      });
      const boundingSphere = Cesium.BoundingSphere.fromPoints(framingPositions);
      const expanded = new Cesium.BoundingSphere(
        boundingSphere.center,
        Math.max(boundingSphere.radius, 200) * 1.3,
      );
      viewer.camera.viewBoundingSphere(
        expanded,
        new Cesium.HeadingPitchRange(
          Cesium.Math.toRadians(20),
          Cesium.Math.toRadians(-35),
          Math.max(expanded.radius * 1.9, 700),
        ),
      );
      viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
      // Only frame the overview bounding-sphere view when there's no active
      // playback camera — otherwise the follow/FPV camera has to race the
      // bounding-sphere framing on every route reload (and occasionally lost,
      // leaving the user staring at the globe until refresh).
      const isInPlayback = progressElapsed !== undefined;
      if (!isInPlayback || cameraMode === "free") {
        const boundingSphere = Cesium.BoundingSphere.fromPoints(framingPositions);
        const expanded = new Cesium.BoundingSphere(
          boundingSphere.center,
          Math.max(boundingSphere.radius, 150) * 1.1,
        );
        viewer.camera.viewBoundingSphere(
          expanded,
          new Cesium.HeadingPitchRange(
            Cesium.Math.toRadians(20),
            Cesium.Math.toRadians(-30),
            Math.max(expanded.radius * 1.5, 500),
          ),
        );
        viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
      } else {
        // Position follow/FPV camera immediately at the first sample so the
        // user doesn't see the default globe view before playback kicks in.
        const first = points[0];
        const pos0 = Cesium.Cartesian3.fromDegrees(
          first.longitude,
          first.latitude,
          first.altitude_m ?? 0,
        );
        const heading0 = Cesium.Math.toRadians(first.yaw_deg ?? 0);
        followContextRef.current = { pos: pos0, headingRad: heading0 };
        if (cameraMode === "follow") {
          applyFollowCameraRef.current();
        } else if (cameraMode === "fpv") {
          viewer.camera.setView({
            destination: Cesium.Cartesian3.fromDegrees(
              first.longitude,
              first.latitude,
              (first.altitude_m ?? 0) + 2.2,
            ),
            orientation: {
              heading: heading0,
              pitch: Cesium.Math.toRadians(first.pitch_deg ?? 0),
              roll: Cesium.Math.toRadians(first.roll_deg ?? 0),
            },
          });
        }
      }
    }
    viewer.scene.requestRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, color, structures, homePoint, backgroundPoints]);

  // Keep the live elapsed/lap refs in sync — Cesium's CallbackProperty reads
  // these on each render frame.
  useEffect(() => {
    elapsedRef.current = progressElapsed ?? 0;
    lapPassesRef.current = lapPasses;
    viewerRef.current?.scene.requestRender();
  }, [lapPasses, progressElapsed]);

  // Progress + camera update
  useEffect(() => {
    const viewer = viewerRef.current;
    const progress = progressRef.current;
    if (!viewer || !progress.marker || progressElapsed === undefined) return;
    if (!points.length) return;

    let idx = 0;
    for (let i = 0; i < points.length; i += 1) {
      if (points[i].elapsed_seconds <= progressElapsed) idx = i;
      else break;
    }
    const a = points[idx];
    const b = points[Math.min(idx + 1, points.length - 1)];
    const span = b.elapsed_seconds - a.elapsed_seconds;
    const t = span > 0 ? (progressElapsed - a.elapsed_seconds) / span : 0;
    const lat = a.latitude + (b.latitude - a.latitude) * t;
    const lon = a.longitude + (b.longitude - a.longitude) * t;
    const alt =
      (a.altitude_m ?? 0) + ((b.altitude_m ?? 0) - (a.altitude_m ?? 0)) * t;
    const yaw = (a.yaw_deg ?? 0) + ((b.yaw_deg ?? 0) - (a.yaw_deg ?? 0)) * t;
    const pitchDeg =
      (a.pitch_deg ?? 0) + ((b.pitch_deg ?? 0) - (a.pitch_deg ?? 0)) * t;
    const rollDeg =
      (a.roll_deg ?? 0) + ((b.roll_deg ?? 0) - (a.roll_deg ?? 0)) * t;
    const pos = Cesium.Cartesian3.fromDegrees(lon, lat, alt);

    progress.positions.length = 0;
    for (let i = 0; i <= idx; i += 1) {
      progress.positions.push(
        Cesium.Cartesian3.fromDegrees(
          points[i].longitude,
          points[i].latitude,
          points[i].altitude_m ?? 0,
        ),
      );
    }
    progress.positions.push(pos);
    progress.marker.position = pos as unknown as Cesium.PositionProperty;

    backgroundProgressRef.current.forEach((bg) => {
      if (!bg.points.length || !bg.marker) return;
      let bgIdx = 0;
      for (let i = 0; i < bg.points.length; i += 1) {
        if (bg.points[i].elapsed_seconds <= progressElapsed) bgIdx = i;
        else break;
      }
      const ba = bg.points[bgIdx];
      const bb = bg.points[Math.min(bgIdx + 1, bg.points.length - 1)];
      const bSpan = bb.elapsed_seconds - ba.elapsed_seconds;
      const bT = bSpan > 0 ? (progressElapsed - ba.elapsed_seconds) / bSpan : 0;
      const bLat = ba.latitude + (bb.latitude - ba.latitude) * bT;
      const bLon = ba.longitude + (bb.longitude - ba.longitude) * bT;
      const bAlt =
        (ba.altitude_m ?? 0) + ((bb.altitude_m ?? 0) - (ba.altitude_m ?? 0)) * bT;
      const bPos = Cesium.Cartesian3.fromDegrees(bLon, bLat, bAlt);
      bg.positions.length = 0;
      for (let i = 0; i <= bgIdx; i += 1) {
        bg.positions.push(
          Cesium.Cartesian3.fromDegrees(
            bg.points[i].longitude,
            bg.points[i].latitude,
            bg.points[i].altitude_m ?? 0,
          ),
        );
      }
      bg.positions.push(bPos);
      bg.marker.position = bPos as unknown as Cesium.PositionProperty;
    });

    const headingRad = Cesium.Math.toRadians(yaw);
    const pitchRad = Cesium.Math.toRadians(pitchDeg);
    const rollRad = Cesium.Math.toRadians(rollDeg);
    // Cesium's HeadingPitchRoll was designed for glTF aircraft models whose
    // forward axis is +X. Our box primitives use ENU-aligned local axes with
    // forward = +Y, so Cesium's "pitch" (rotation around -Y = around forward
    // axis) ends up rolling the body, and its "roll" (rotation around +X =
    // around right-wing axis) ends up pitching it. We compensate by swapping
    // aerospace pitch↔roll when constructing HPR. The sign on roll flips
    // because Cesium pitch rotates around -Y, so positive Cesium pitch rolls
    // the right wing up — the opposite of aerospace positive roll.
    const hpr = new Cesium.HeadingPitchRoll(headingRad, -rollRad, pitchRad);
    const bodyOrient = Cesium.Transforms.headingPitchRollQuaternion(pos, hpr);
    const localFrame = Cesium.Transforms.headingPitchRollToFixedFrame(pos, hpr);

    // Local body frame after HPR: X=right wing, Y=nose/forward, Z=up.
    // Prop arms extend in an X pattern. Using ± wingspan/2 on X and ±
    // length/2 on Y, slightly above the body on Z.
    const armX = 1.15; // half wingspan
    const armY = 1.15; // half length
    const armZ = 0.22; // above body
    const canopyZ = 0.32; // canopy sits on top of the body
    const offsetToWorld = (x: number, y: number, z: number): Cesium.Cartesian3 =>
      Cesium.Matrix4.multiplyByPoint(
        localFrame,
        new Cesium.Cartesian3(x, y, z),
        new Cesium.Cartesian3(),
      );

    // Update the live pose ref — CallbackProperty reads these values every
    // render frame, which is what makes the body and props move continuously.
    const pose = dronePoseRef.current;
    pose.pos = pos;
    pose.orientation = bodyOrient;
    pose.canopy = offsetToWorld(0, 0, canopyZ);
    pose.propFR = offsetToWorld(+armX, +armY, armZ);
    pose.propFL = offsetToWorld(-armX, +armY, armZ);
    pose.propRR = offsetToWorld(+armX, -armY, armZ);
    pose.propRL = offsetToWorld(-armX, -armY, armZ);
    const shouldShow = cameraMode !== "fpv";
    pose.show = shouldShow;
    // Entity.show is read from the initial CallbackProperty, but visibility
    // toggles happen rarely (user switches camera mode) so we also assign the
    // boolean directly to defeat any caching inside Cesium's primitive layer.
    // Marker must hide too — otherwise the 10px point sits right in the FPV
    // camera's eye and blocks the view.
    if (progress.body) progress.body.show = shouldShow;
    if (progress.canopy) progress.canopy.show = shouldShow;
    if (progress.nose) progress.nose.show = shouldShow;
    if (progress.marker) progress.marker.show = shouldShow;
    if (progress.propsFR) progress.propsFR.show = shouldShow;
    if (progress.propsFL) progress.propsFL.show = shouldShow;
    if (progress.propsRR) progress.propsRR.show = shouldShow;
    if (progress.propsRL) progress.propsRL.show = shouldShow;

    if (cameraMode !== "free") {
      viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
      if (cameraMode === "fpv") {
        const eye = Cesium.Cartesian3.fromDegrees(lon, lat, alt + 2.2);
        viewer.camera.setView({
          destination: eye,
          orientation: { heading: headingRad, pitch: pitchRad, roll: rollRad },
        });
      } else if (cameraMode === "follow") {
        // Snapshot heading/pos so the mouse-drag handler can re-frame the
        // camera without waiting for the next playback tick.
        followContextRef.current = { pos, headingRad };
        applyFollowCameraRef.current();
      }
    }
    viewer.scene.requestRender();
  }, [progressElapsed, points, cameraMode]);

  // Keep the follow-camera applier in sync — reads offset + context refs, so
  // both the playback loop and the drag handler can call it.
  applyFollowCameraRef.current = () => {
    const viewer = viewerRef.current;
    const ctx = followContextRef.current;
    if (!viewer || !ctx) return;
    const { pos, headingRad } = ctx;
    // Default tilt: ~13° above horizontal (the old backDist=35/upDist=8 ratio).
    // User-adjustable pitch offset is added on top, in degrees.
    const DEFAULT_FOLLOW_PITCH_DEG = 13;
    const followDist = 36;
    const yawOffset = followOffsetRef.current.yaw;
    const pitchDeg = Math.max(
      -25,
      Math.min(75, DEFAULT_FOLLOW_PITCH_DEG + followOffsetRef.current.pitch),
    );
    const pitchRad = Cesium.Math.toRadians(pitchDeg);
    const effHeading = headingRad + yawOffset;
    const hDist = followDist * Math.cos(pitchRad);
    const vDist = followDist * Math.sin(pitchRad);
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(pos);
    const offsetLocal = new Cesium.Cartesian3(
      -hDist * Math.sin(effHeading),
      -hDist * Math.cos(effHeading),
      vDist,
    );
    const cameraPos = Cesium.Matrix4.multiplyByPoint(
      enu,
      offsetLocal,
      new Cesium.Cartesian3(),
    );
    viewer.camera.setView({
      destination: cameraPos,
      orientation: {
        heading: effHeading,
        pitch: -pitchRad,
        roll: 0,
      },
    });
  };

  // Mouse-drag orbit in follow mode. Horizontal drag rotates the camera around
  // the drone; vertical drag raises/lowers the viewing angle. Double-click
  // resets to the default behind-above framing.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || cameraMode !== "follow") return;
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    handler.setInputAction((ev: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      dragging = true;
      lastX = ev.position.x;
      lastY = ev.position.y;
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);
    handler.setInputAction((ev: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      if (!dragging) return;
      const dx = ev.endPosition.x - lastX;
      const dy = ev.endPosition.y - lastY;
      lastX = ev.endPosition.x;
      lastY = ev.endPosition.y;
      // Drag right → rotate camera clockwise around drone (view shifts left).
      followOffsetRef.current.yaw -= dx * 0.006;
      // Drag down → lower the viewpoint (less tilt-down); drag up → more tilt.
      followOffsetRef.current.pitch -= dy * 0.25;
      followOffsetRef.current.pitch = Math.max(
        -30,
        Math.min(55, followOffsetRef.current.pitch),
      );
      applyFollowCameraRef.current();
      viewer.scene.requestRender();
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
    handler.setInputAction(() => {
      dragging = false;
    }, Cesium.ScreenSpaceEventType.LEFT_UP);
    handler.setInputAction(() => {
      followOffsetRef.current = { yaw: 0, pitch: 0 };
      applyFollowCameraRef.current();
      viewer.scene.requestRender();
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    return () => handler.destroy();
  }, [cameraMode]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer?.camera?.frustum) return;
    const frustum = viewer.camera.frustum as unknown as { fov?: number };
    const clamped = Math.min(115, Math.max(35, fov));
    frustum.fov = Cesium.Math.toRadians(clamped);
    viewer.scene.requestRender();
  }, [fov]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.imageryLayers.removeAll();
    if (mapStyle === "classic") {
      try {
        const osm = new Cesium.OpenStreetMapImageryProvider({
          url: "https://tile.openstreetmap.org/",
        });
        osm.errorEvent.addEventListener(() => {});
        viewer.imageryLayers.addImageryProvider(osm);
      } catch {
        /* ignore */
      }
    } else {
      try {
        const esri = new Cesium.UrlTemplateImageryProvider({
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          maximumLevel: 19,
          credit: "Esri, Maxar, Earthstar Geographics",
        });
        esri.errorEvent.addEventListener(() => {});
        viewer.imageryLayers.addImageryProvider(esri);
      } catch {
        /* ignore */
      }
    }
    viewer.scene.requestRender();
  }, [mapStyle]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      viewerRef.current?.resize();
      viewerRef.current?.scene.requestRender();
    }, 120);
    return () => window.clearTimeout(timer);
  }, [height]);

  // Track container size — covers fullscreen enter/exit and any layout shift
  // (Cesium otherwise keeps the canvas at the previous dimensions).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const viewer = viewerRef.current;
      if (!viewer || viewer.isDestroyed()) return;
      viewer.resize();
      viewer.scene.requestRender();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="cesium-map-surface"
      style={{ height, width: "100%", position: "relative" }}
    />
  );
}
